import { z } from 'zod'

// PBN capability, ICAO flight plan item 18 PBN/ (Doc 4444 / Doc 9613). A
// light aircraft's usual set is A1, B2, C2, D2, O2, S1; the list is the whole
// table so a club aircraft with something rarer can be entered.
export const PBN_CODES = [
  'A1',
  'B1', 'B2', 'B3', 'B4', 'B5',
  'C1', 'C2', 'C3', 'C4',
  'D1', 'D2', 'D3', 'D4',
  'L1',
  'O1', 'O2', 'O3', 'O4',
  'S1', 'S2',
  'T1', 'T2',
] as const
export type PbnCode = (typeof PBN_CODES)[number]

// Item 18 allows at most eight codes in PBN/.
export const PBN_MAX_CODES = 8

export const PbnSchema = z.array(z.enum(PBN_CODES)).max(PBN_MAX_CODES)

// PBN is only meaningful for an aircraft with RNAV equipment: item 10a
// letter R. Case-insensitive, like the rest of the equipment string.
export const equipmentHasPbn = (equipment: string | null | undefined): boolean =>
  (equipment ?? '').toUpperCase().includes('R')

/** Item 18's PBN/ entry, e.g. "PBN/B2O2S1"; '' when there is nothing to say. */
export function pbnItem18(equipment: string | null | undefined, pbn: ReadonlyArray<string> | null | undefined): string {
  return equipmentHasPbn(equipment) && pbn && pbn.length > 0 ? `PBN/${pbn.join('')}` : ''
}

// A row of GET /api/aircraft/:registration/power-settings.
export interface AircraftPowerSetting {
  phase: string
  rpm: number | null
  manifoldInhg: number | null
  powerPercent: number | null
  fuelFlowUsg: number | null
  tasKt: number | null
}

const optional = <T extends z.ZodTypeAny>(schema: T) => schema.nullable().default(null)

export const PowerSettingSchema = z.object({
  phase: z.string().trim().min(1).max(60),
  rpm: optional(z.number().int().positive().max(9999)),
  manifoldInhg: optional(z.number().positive().max(99.9)),
  powerPercent: optional(z.number().int().min(1).max(100)),
  fuelFlowUsg: optional(z.number().nonnegative().max(999.9)),
  tasKt: optional(z.number().int().positive().max(999)),
})

// The whole set, replaced at once, in the order shown.
export const PowerSettingsSchema = z.object({ settings: z.array(PowerSettingSchema).max(20) })
export type PowerSettingsInput = z.infer<typeof PowerSettingsSchema>

// Secrets the club keeps per aircraft (intra.secrets). Today only the fuel
// card's PIN; the category column is there for the others.
export const SECRET_CATEGORIES = ['fuelCardPin'] as const
export type SecretCategory = (typeof SECRET_CATEGORIES)[number]

// How long the fuel card PIN stays on screen once a pilot opens it.
export const PIN_REVEAL_SECONDS = 10

// A row of GET /api/admin/secrets.
export interface AircraftSecret {
  category: SecretCategory
  registration: string | null
  value: string
  updatedAt: string
}

export const SecretValueSchema = z.object({ value: z.string().trim().min(1).max(40) })
